import { ComponentProps } from 'react';

/**
 * A unit plat: the unit outline, its allocation tracts with labels, and the
 * wellbore, all drawn in ONE shared coordinate space.
 *
 * Sibling of PolygonSvg, which renders a single geometry normalised against its own
 * bounding box. That is the wrong primitive here — rendering four tracts through it
 * separately would scale each to fill its own viewport and destroy their spatial
 * relationship. PlatSvg computes one bounding box across every geometry and projects
 * all of them through it.
 *
 * Longitude degrees are shorter than latitude degrees away from the equator, so x is
 * scaled by cos(latitude) before fitting. Without that correction a square section
 * renders ~18% too wide at the ~32°N of the Permian.
 */

interface PlatTract {
  geometry: GeoJSON.Geometry | null;
  label?: string | null;
}

interface Props extends Omit<ComponentProps<'svg'>, 'width' | 'height'> {
  /** Allocation tracts, drawn filled with their labels centred. */
  tracts: PlatTract[];
  /** The unit boundary, drawn as an unfilled outline. */
  unitGeometry?: GeoJSON.Geometry | null;
  /** The wellbore, drawn as a line with a dot at its first vertex. */
  wellGeometry?: GeoJSON.Geometry | null;
  width?: number;
  height?: number;
  padding?: number;
}

type Ring = number[][];

/**
 * Flattens any GeoJSON geometry to the flat list of rings/lines we draw.
 *
 * @param {GeoJSON.Geometry | null | undefined} geometry - Geometry to flatten.
 * @return {Ring[]} One entry per polygon ring or line; empty for unsupported types.
 */
const toRings = (geometry: GeoJSON.Geometry | null | undefined): Ring[] => {
  if (!geometry) return [];
  switch (geometry.type) {
    case 'Polygon':
      return geometry.coordinates as Ring[];
    case 'MultiPolygon':
      return (geometry.coordinates as number[][][][]).flat() as Ring[];
    case 'LineString':
      return [geometry.coordinates as Ring];
    case 'MultiLineString':
      return geometry.coordinates as Ring[];
    default:
      return [];
  }
};

/**
 * Average vertex position across a geometry's rings, used to place a tract label.
 *
 * A GeoJSON polygon ring is closed — its last vertex repeats its first — so a plain
 * average counts that corner twice and drags the label toward it, noticeably on a
 * long thin tract. The closing vertex is dropped per ring before averaging.
 *
 * @param {Ring[]} rings - Rings to average.
 * @return {[number, number] | null} Mean [lon, lat], or null when there are no points.
 */
const ringCentroid = (rings: Ring[]): [number, number] | null => {
  const points = rings
    .flatMap((ring) => {
      const last = ring[ring.length - 1];
      const first = ring[0];
      const closed =
        ring.length > 1 && last[0] === first[0] && last[1] === first[1];
      return closed ? ring.slice(0, -1) : ring;
    })
    .filter(Boolean);
  if (points.length === 0) return null;
  const sum = points.reduce(
    (acc, [lon, lat]) => [acc[0] + lon, acc[1] + lat],
    [0, 0]
  );
  return [sum[0] / points.length, sum[1] / points.length];
};

// Carbon theme tokens with the previous literal as fallback. ui-04,
// interactive-01, text-01 and text-02 are the exact values that were hardcoded
// here; only the tract fill has no semantic equivalent and keeps its own.
//
// Applied through `style`, NOT as SVG presentation attributes. A presentation
// attribute is parsed against the property's value grammar rather than as a CSS
// declaration, so there is no cascade-time step for var() to resolve in — engines
// differ, and Chromium is the strict one. The failure mode is the bad one: a value
// that does not parse as <paint> makes the attribute ignored and fill falls back to
// its INITIAL value (black), not to the literal inside the var(), because the
// fallback only applies once substitution runs at all. A style declaration resolves
// everywhere, which is what field-verify-icon.tsx relies on.
const TRACT_FILL = 'var(--cds-ui-03, #e8eaf6)';
const TRACT_STROKE = 'var(--cds-ui-04, #8d8d8d)';
const UNIT_STROKE = 'var(--cds-interactive-01, #0f62fe)';
const WELL_STROKE = 'var(--cds-text-01, #161616)';
const LABEL_FILL = 'var(--cds-text-02, #525252)';

export const PlatSvg = ({
  tracts,
  unitGeometry,
  wellGeometry,
  width = 260,
  height = 260,
  padding = 12,
  ...svgProps
}: Props) => {
  const tractRings = tracts.map((tract) => toRings(tract.geometry));
  const unitRings = toRings(unitGeometry);
  const wellRings = toRings(wellGeometry);

  const allPoints = [...tractRings.flat(), ...unitRings, ...wellRings].flat();
  if (allPoints.length === 0) {
    return <svg width={width} height={height} {...svgProps} />;
  }

  const lons = allPoints.map((c) => c[0]);
  const lats = allPoints.map((c) => c[1]);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  // cos of the mid-latitude: how much shorter a longitude degree is here.
  const lonScale = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180) || 1;

  const spanX = (maxLon - minLon) * lonScale;
  const spanY = maxLat - minLat;
  const scale = Math.min(
    (width - 2 * padding) / (spanX || 1),
    (height - 2 * padding) / (spanY || 1)
  );

  // Centre the drawing in the viewport rather than pinning it to a corner.
  const offsetX = padding + (width - 2 * padding - spanX * scale) / 2;
  const offsetY = padding + (height - 2 * padding - spanY * scale) / 2;

  const projectX = (lon: number) => offsetX + (lon - minLon) * lonScale * scale;
  // SVG y grows downward, latitude grows upward.
  const projectY = (lat: number) => height - offsetY - (lat - minLat) * scale;

  const points = (ring: Ring) =>
    ring.map(([lon, lat]) => `${projectX(lon)},${projectY(lat)}`).join(' ');

  return (
    <svg width={width} height={height} {...svgProps}>
      {tractRings.map((rings, tractIdx) =>
        rings.map((ring, ringIdx) => (
          <polygon
            key={`tract-${tractIdx}-${ringIdx}`}
            points={points(ring)}
            style={{ fill: TRACT_FILL, stroke: TRACT_STROKE }}
            strokeWidth="1"
          />
        ))
      )}

      {unitRings.map((ring, idx) => (
        <polygon
          key={`unit-${idx}`}
          points={points(ring)}
          fill="none"
          style={{ stroke: UNIT_STROKE }}
          strokeWidth="1.5"
        />
      ))}

      {wellRings.map((ring, idx) => (
        <g key={`well-${idx}`}>
          <polyline
            points={points(ring)}
            fill="none"
            style={{ stroke: WELL_STROKE }}
            strokeWidth="1.5"
          />
          {ring.length > 0 && (
            <circle
              cx={projectX(ring[0][0])}
              cy={projectY(ring[0][1])}
              r="3"
              style={{ fill: WELL_STROKE }}
            />
          )}
        </g>
      ))}

      {tractRings.map((rings, idx) => {
        const label = tracts[idx].label;
        const centre = ringCentroid(rings);
        if (!label || !centre) return null;
        return (
          <text
            key={`label-${idx}`}
            x={projectX(centre[0])}
            y={projectY(centre[1])}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            style={{ fill: LABEL_FILL }}
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
};
